/**
 * MAHI discovery feed — full-screen photo viewer, opened by tapping a card
 * (see DiscoveryFeed's handleTap). Photos only, no product copy — this is
 * "look closer at the picture," not a product detail page. The ambient
 * crossfade slideshow lives on the card itself now; here navigation and
 * zoom are fully user-driven, matching how a native photo viewer behaves.
 *
 * Left/right thirds of the screen step through photos (mirrors the tap
 * zones already used for photo browsing on Home.jsx's hero stage), and so
 * do the Left/Right arrow keys — Escape closes. Double-tap/double-click
 * toggles zoom; a pinch (tracked via raw touch events, since framer-motion
 * has no built-in multi-touch gesture) scales continuously between 1x and
 * MAX_SCALE, and the on-screen +/- buttons step the same scale value for
 * anyone without a trackpad/touchscreen pinch handy. Nav zones switch off
 * while zoomed — panning takes over via drag — and reappear once zoom
 * resets.
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue } from 'framer-motion';
import { Minus, Plus, X } from 'lucide-react';

const MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_WINDOW_MS = 280;
const ZOOM_STEP = 0.6;

function touchDistance(touches) {
  const [a, b] = touches;
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

export default function ProductDetailView({ product, onClose }) {
  const images = product.image_urls?.length ? product.image_urls : [];
  const title = product.display_title || product.title;

  const [index, setIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const isZoomed = zoomLevel > 1.05;
  const scale = useMotionValue(1);
  const panX = useMotionValue(0);
  const panY = useMotionValue(0);
  const pinch = useRef({ active: false, startDist: 0, startScale: 1 });
  const lastTap = useRef(0);

  useEffect(() => {
    const unsubscribe = scale.on('change', setZoomLevel);
    return unsubscribe;
  }, [scale]);

  const resetZoom = () => { scale.set(1); panX.set(0); panY.set(0); };

  const goPrev = () => { if (isZoomed) return; resetZoom(); setIndex((i) => (i - 1 + images.length) % images.length); };
  const goNext = () => { if (isZoomed) return; resetZoom(); setIndex((i) => (i + 1) % images.length); };

  const toggleZoom = () => {
    if (scale.get() > 1) resetZoom();
    else scale.set(DOUBLE_TAP_SCALE);
  };

  const zoomIn = () => scale.set(Math.min(MAX_SCALE, scale.get() + ZOOM_STEP));
  const zoomOut = () => {
    const next = Math.max(1, scale.get() - ZOOM_STEP);
    scale.set(next);
    if (next <= 1) { panX.set(0); panY.set(0); }
  };

  // Escape closes, Left/Right step through photos — same behavior as the
  // tap zones, gated the same way (disabled while zoomed).
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isZoomed, images.length, onClose]);

  const handleTap = () => {
    const now = Date.now();
    if (now - lastTap.current < DOUBLE_TAP_WINDOW_MS) toggleZoom();
    lastTap.current = now;
  };

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinch.current = { active: true, startDist: touchDistance(e.touches), startScale: scale.get() };
    }
  };
  const onTouchMove = (e) => {
    if (pinch.current.active && e.touches.length === 2) {
      const d = touchDistance(e.touches);
      const next = Math.min(MAX_SCALE, Math.max(1, pinch.current.startScale * (d / pinch.current.startDist)));
      scale.set(next);
    }
  };
  const onTouchEnd = (e) => {
    if (e.touches.length < 2) pinch.current.active = false;
    if (scale.get() < 1.05) resetZoom();
  };

  return (
    <motion.div
      data-testid="product-detail-view"
      // `touch-none` here, not just on the image below: `object-contain`
      // routinely leaves the image narrower or shorter than this frame
      // (letterboxed), and that margin is still `body`'s default
      // `touch-action: manipulation` (see index.css) — which keeps real
      // pinch-zoom on elsewhere in the app. A pinch that starts with either
      // finger over the black background instead of the photo used to zoom
      // the actual page natively while this component's own touch handlers
      // *also* scaled the image, leaving the whole viewport zoomed and
      // panning independently of anything on screen — indistinguishable
      // from the app having hung. Locking the whole viewer, not just the
      // image, is what index.css's own framer-motion comment describes as
      // "which still wins where it matters."
      className="fixed inset-0 z-50 bg-black flex items-center justify-center overflow-hidden touch-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {images.length > 1 && (
        <div className="absolute top-4 inset-x-4 flex gap-1.5 z-20">
          {images.map((_, i) => (
            <div key={i} className="flex-1 h-1 rounded-full bg-white/30 overflow-hidden">
              <div className={`h-full bg-white transition-[width] duration-300 ${i <= index ? 'w-full' : 'w-0'}`} />
            </div>
          ))}
        </div>
      )}

      <button type="button" onClick={onClose} aria-label="Close" data-testid="detail-close"
        className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center active:scale-95 transition-transform">
        <X className="w-5 h-5" />
      </button>

      <motion.img
        key={images[index]}
        src={images[index]}
        alt={title}
        draggable={false}
        className="max-w-full max-h-full object-contain select-none touch-none"
        style={{ scale, x: panX, y: panY }}
        drag={isZoomed}
        dragElastic={0.15}
        onTap={handleTap}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      />

      {images.length > 1 && !isZoomed && (
        <>
          <button type="button" onClick={goPrev} aria-label="Previous photo"
            className="absolute inset-y-0 left-0 w-1/3 z-10" />
          <button type="button" onClick={goNext} aria-label="Next photo"
            className="absolute inset-y-0 right-0 w-1/3 z-10" />
        </>
      )}

      {/* Zoom controls — sit in the middle third so they never overlap the
          left/right nav zones above, and stay put (don't disappear when
          zoomed, unlike the nav zones) since that's exactly when you'd
          reach for them. 44px targets for a comfortable thumb tap. */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-full p-1">
        <button type="button" onClick={zoomOut} disabled={zoomLevel <= 1.05} aria-label="Zoom out"
          data-testid="detail-zoom-out"
          className="w-11 h-11 rounded-full flex items-center justify-center text-white disabled:opacity-30 active:scale-95 transition-transform">
          <Minus className="w-5 h-5" />
        </button>
        <button type="button" onClick={zoomIn} disabled={zoomLevel >= MAX_SCALE - 0.05} aria-label="Zoom in"
          data-testid="detail-zoom-in"
          className="w-11 h-11 rounded-full flex items-center justify-center text-white disabled:opacity-30 active:scale-95 transition-transform">
          <Plus className="w-5 h-5" />
        </button>
      </div>
    </motion.div>
  );
}
